import React, {FC, useEffect} from 'react';

import GooglePayButton from '@google-pay/button-react';
import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {Icon, GooglePayD} from '@optimaxdev/icons';
import classNames from 'classnames';
import {connect} from 'react-redux';

import {useFeature} from 'components/feature';
import {CHECKOUT_PAYMENT_STEP} from 'constants/analytics/events/checkout';
import {PlacingOrderScreen} from 'desktop/components/placingOrderScreen/placingOrderScreen';
import {ApplicationStoreType} from 'desktop/store';
import {callbackWithAnalyticsEvent} from 'libs/analytics/callbackWithAnalytic';
import {paymentGooglePayConfig} from 'libs/google/paymentGooglePayConfig';
import {PaymentType} from 'types/PaymentMethodsType';

import {PAYMENTS} from '../../constants/paymentStep';
import {setOptionPaymentMethod} from '../../store/reducers/analytics/analytics';
import {savePaymentMethod} from '../../store/reducers/paymentStep/paymentMethodSave/paymentMethodSave';
import {
    getGooglePayMessageError,
    getPaymentMethodSaveLoading,
} from '../../store/selectors/paymentStep/paymentMethodSave/paymentMethodSave';
import {
    getIsSelectedByKey,
    getPaymentMethodByKey,
    getTotalsForGooglePay,
} from '../../store/selectors/paymentStep/paymentStep';
import {PaymentStepAgreement} from '../paymentStepAgreement/paymentStepAgreement';
import {PaymentWrapperConnected} from '../paymentWrapper/paymentWrapper';

import {googlePayOnCancel, googlePayOnError} from './helpers/paymentGooglePayHalpers';

import s from './paymentGooglePay.scss';

export type PaymentGooglePayType = {
    isSelected: boolean;
    method: PaymentType;
    setOptionPayment: () => void;
    save: ({method, token}: {token: string; method: string}) => void;
    isLoading: boolean;
    totals: {total: string};
    errorMessage?: string | boolean;
};

type ParsedGooglePayPaymentDataType = {
    id: string;
    type: 'card' | string;
};

/**
 * Payment GooglePay for checkout
 */
export const PaymentGooglePay: FC<PaymentGooglePayType> = ({
    errorMessage,
    isLoading,
    isSelected,
    method,
    save,
    setOptionPayment,
    totals,
}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const onApprove = async (paymentRequest: google.payments.api.PaymentData) => {
        const parsedData: ParsedGooglePayPaymentDataType = JSON.parse(
            paymentRequest.paymentMethodData.tokenizationData.token,
        );
        await callbackWithAnalyticsEvent('place order', 'Google Pay', async () => {
            save({method: PAYMENTS.METHOD_GOOGLE_PAY, token: parsedData.id});
        });
    };

    useEffect(() => {
        if (errorMessage) {
            sendDesktopAnalyticsEvent(
                CHECKOUT_PAYMENT_STEP,
                {
                    message: `Error – ${errorMessage}}`,
                    method: 'Google Pay',
                },
                false,
            );
        }
    }, [errorMessage]);

    const {code, is_sandbox: isSandbox, publishable_key: publishableKey, version} = method;
    if (!publishableKey || !version) return null;

    const onClick = () => {
        sendDesktopAnalyticsEvent(
            CHECKOUT_PAYMENT_STEP,
            {
                message: 'CTA - Google Pay',
                method: 'Payment',
            },
            true,
        );
        setOptionPayment();
    };

    return (
        <PaymentWrapperConnected
            title={
                <div className={classNames(s.title, {[s.titleFacelift]: isFacelift})}>
                    <Icon icon={GooglePayD} size="responsive" />
                </div>
            }
            code={code}
            isSelected={isSelected}
        >
            <>
                <div className={classNames(s.margin, {[s.marginFacelift]: isFacelift})}>
                    <p className={classNames(s.text, {[s.textFacelift]: isFacelift})}>
                        Click the button to be redirected to Google Pay & complete your purchase.
                    </p>
                    <PaymentStepAgreement
                        className={classNames(s.text, {[s.textFacelift]: isFacelift})}
                    />
                </div>

                <GooglePayButton
                    paymentRequest={paymentGooglePayConfig({
                        merchantId: method.merchant_id || '',
                        merchantName: method.merchant_name || '',
                        platform: 'desktop',
                        stripe: {publishableKey, version},
                        totalPrice: totals.total,
                    })}
                    buttonSizeMode="fill"
                    environment={isSandbox === '1' ? 'TEST' : 'PRODUCTION'}
                    onCancel={googlePayOnCancel}
                    onClick={onClick}
                    onError={googlePayOnError}
                    onLoadPaymentData={onApprove}
                    style={{height: 48, width: 334}}
                />
                <p className={classNames(s.text, errorMessage && s.errorMessage)}>{errorMessage}</p>

                {isLoading && <PlacingOrderScreen />}
            </>
        </PaymentWrapperConnected>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    errorMessage: getGooglePayMessageError(store),
    isLoading: getPaymentMethodSaveLoading(store),
    isSelected: getIsSelectedByKey(store, PAYMENTS.METHOD_GOOGLE_PAY),
    method: getPaymentMethodByKey(store, PAYMENTS.METHOD_GOOGLE_PAY),
    totals: getTotalsForGooglePay(store),
});

export const PaymentGooglePayConnected = connect(mapStateToProps, {
    save: savePaymentMethod,
    setOptionPayment: setOptionPaymentMethod,
})(PaymentGooglePay);
